import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-3 whitespace-nowrap rounded-full text-sm font-medium transition-all duration-200 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-offset-2 cursor-pointer",
  {
    variants: {
      variant: {
        default:
          'bg-button-primary-default text-button-primary-text hover:shadow-[0px_2px_4px_0px_rgba(0,0,0,0.15)] active:shadow-[0px_4px_8px_0px_rgba(0,0,0,0.2)] disabled:bg-button-primary-default/40 disabled:text-button-primary-disabled-text',
        secondary:
          'bg-white font-normal border border-gray-300 hover:bg-gray-50 focus-visible:ring-gray-500/20 hover:shadow-md disabled:bg-[#F7F8F9] disabled:text-text-disabled',
        destructive: 'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500/20 shadow-sm hover:shadow-md',
        outline: 'border border-gray-300 bg-white hover:bg-gray-50 focus-visible:ring-gray-500/20 shadow-sm',
        ghost: 'text-text-secondary hover:bg-background-hover focus-visible:ring-gray-500/20',
        link: 'text-text-link underline-offset-4 hover:underline focus-visible:ring-blue-500/20 !p-0 !h-fit',
      },
      size: {
        default: 'h-9 px-5 md:px-6 py-3 text-sm',
        sm: 'h-8 px-4 py-2 text-xs',
        lg: 'h-12 px-8 py-4 text-base',
        icon: 'h-12 w-12',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)
export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    loading?: boolean
    icon?: React.ReactNode
    trailingIcon?: React.ReactNode
    href?: string
  }
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, loading = false, icon, trailingIcon, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'

    if (asChild) {
      return (
        <Comp ref={ref} data-slot='button' className={cn(buttonVariants({ variant, size, className }))} {...props}>
          {children}
        </Comp>
      )
    }

    return (
      <Comp ref={ref} data-slot='button' className={cn(buttonVariants({ variant, size, className }))} {...props}>
        {loading && (
          <svg className='mr-2 -ml-1 h-4 w-4 animate-spin' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
            <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
            <path
              className='opacity-75'
              fill='currentColor'
              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
            />
          </svg>
        )}
        {icon && !loading && icon}
        {children}
        {trailingIcon && !loading && trailingIcon}
      </Comp>
    )
  }
)

Button.displayName = 'Button'

export { Button, buttonVariants }
